import api from './api';
import type {
  Claim,
  ClaimCreateRequest,
  ClaimFilters,
  ClaimDocument,
  ClaimTimeline,
  PaginatedResponse,
} from '@/types';

export interface ClaimTypeOption {
  value: string;
  label: string;
}

export const claimService = {
  /**
   * The claim types the API actually accepts.
   *
   * Previously the form hardcoded its own list and 6 of its 8 options were not
   * valid CLAIM_TYPE_CHOICES values, so choosing one always failed with
   * "not a valid choice". Sourcing them from the server is what stops the two
   * drifting apart again.
   */
  async getClaimTypes(): Promise<ClaimTypeOption[]> {
    const response = await api.get<ClaimTypeOption[]>('/claims/types/');
    return response.data;
  },

  /**
   * B7: the legal next statuses for each claim status.
   *
   * Same lesson as getClaimTypes above: the detail page hardcoded all statuses
   * in one flat dropdown, and one of them (`under_investigation`) is not even a
   * valid STATUS_CHOICES value. The server owns the graph
   * (`ClaimService.STATUS_TRANSITIONS`) and still validates every transition,
   * so this only decides what the client OFFERS.
   */
  async getStatusTransitions(): Promise<Record<string, ClaimTypeOption[]>> {
    const response = await api.get<Record<string, ClaimTypeOption[]>>('/claims/transitions/');
    return response.data;
  },

  async getClaims(page: number = 1, filters?: ClaimFilters): Promise<PaginatedResponse<Claim>> {
    const params = new URLSearchParams();
    params.append('page', page.toString());

    if (filters?.customer) params.append('customer', filters.customer);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.status_in) params.append('status__in', filters.status_in);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.claim_type) params.append('claim_type', filters.claim_type);
    // #401: the insurer Claims Desk queue tabs. The server reads these for
    // insurer roles only.
    if (filters?.awaiting_my_decision) params.append('awaiting_my_decision', 'true');
    if (filters?.has_open_document_requests) params.append('has_open_document_requests', 'true');
    // WARNING -- THE SERVER READS NEITHER OF THESE (as of 2026-09-20).
    // `ClaimViewSet` has `filterset_fields = ["status", "claim_type"]` and no
    // `filterset_class`, and `get_queryset` hand-handles only `status__in` and
    // `customer`; nothing in the backend mentions `incident_date_after` or
    // `incident_date_before`, and django-filter drops an unknown parameter in
    // silence. The claims list's incident-date window was REMOVED for exactly
    // that reason -- there is no caller left. These two lines are kept as the
    // seam a backend `DateFromToRangeFilter` on `incident_date` would plug
    // straight into; do not wire a control to them before that exists.
    if (filters?.date_from) params.append('incident_date_after', filters.date_from);
    if (filters?.date_to) params.append('incident_date_before', filters.date_to);

    const response = await api.get<PaginatedResponse<Claim>>(`/claims/?${params.toString()}`);
    return response.data;
  },

  async getClaim(id: string): Promise<Claim> {
    const response = await api.get<Claim>(`/claims/${id}/`);
    return response.data;
  },

  async createClaim(data: ClaimCreateRequest): Promise<Claim> {
    const response = await api.post<Claim>('/claims/', data);
    return response.data;
  },

  async updateClaim(id: string, data: Partial<ClaimCreateRequest>): Promise<Claim> {
    const response = await api.patch<Claim>(`/claims/${id}/`, data);
    return response.data;
  },

  async deleteClaim(id: string): Promise<void> {
    await api.delete(`/claims/${id}/`);
  },

  async updateStatus(id: string, status: string, notes?: string): Promise<Claim> {
    const response = await api.post<Claim>(`/claims/${id}/update_status/`, { status, notes });
    return response.data;
  },

  async assignSurveyor(id: string, surveyorId: string): Promise<Claim> {
    const response = await api.post<Claim>(`/claims/${id}/assign_surveyor/`, { surveyor: surveyorId });
    return response.data;
  },

  async getDocuments(claimId: string): Promise<ClaimDocument[]> {
    const response = await api.get<ClaimDocument[]>(`/claims/${claimId}/documents/`);
    return response.data;
  },

  async uploadDocument(claimId: string, formData: FormData): Promise<ClaimDocument> {
    const response = await api.post<ClaimDocument>(`/claims/${claimId}/documents/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async deleteDocument(claimId: string, documentId: string): Promise<void> {
    await api.delete(`/claims/${claimId}/documents/${documentId}/`);
  },

  async getTimeline(claimId: string): Promise<ClaimTimeline[]> {
    const response = await api.get<ClaimTimeline[]>(`/claims/${claimId}/timeline/`);
    return response.data;
  },
};
